import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createLinkNode, $isLinkNode } from '@synoptic/lexical-nodes/link.ts';
import { LinkButton } from '@synoptic/ui-kit/link-button/link-button.js';
import {
  $createTextNode,
  $getRoot,
  $isDecoratorNode,
  $isElementNode,
  $isTextNode,
  $parseSerializedNode,
  $setSelection,
  LexicalNode,
  RootNode,
  SerializedRootNode,
  TextNode,
} from 'lexical';
import { useLayoutEffect, useState } from 'react';
import { toLinkUrl, URL_REGEX } from '../link-url.ts';

const ELLIPSIS = '...';

const ELLIPSIS_RE = /\.\.\.$/;

const stripTrailingEllipsis = (text: string): string =>
  text.replace(ELLIPSIS_RE, '');

const $getBudgetTextLength = (node: LexicalNode): number => {
  if ($isTextNode(node)) {
    return stripTrailingEllipsis(node.getTextContent()).replaceAll('\n', '')
      .length;
  }
  if ($isElementNode(node)) {
    let total = 0;
    for (const child of node.getChildren()) {
      total += $getBudgetTextLength(child);
    }
    return total;
  }
  if ($isDecoratorNode(node)) {
    return node.getTextContent().length;
  }
  return 0;
};

const URL_REGEX_GLOBAL = new RegExp(URL_REGEX.source, 'g');

const $cutInsideUrl = (node: TextNode, text: string, cut: number): boolean => {
  if ($isLinkNode(node.getParent())) return false;
  for (const match of text.matchAll(URL_REGEX_GLOBAL)) {
    const start = match.index;
    const end = start + match[0].length;
    if (cut <= start || cut > end) continue;

    const link = $createLinkNode(toLinkUrl(match[0]));
    link.append(
      $createTextNode(text.slice(start, cut)).setFormat(node.getFormat()),
    );
    node.insertAfter(link);
    link.insertAfter($createTextNode(ELLIPSIS).setFormat(node.getFormat()));
    if (start === 0) node.remove();
    else node.setTextContent(text.slice(0, start));
    return true;
  }
  return false;
};

const $truncateRoot = (rootNode: RootNode, charLimit: number): void => {
  if ($getBudgetTextLength(rootNode) <= charLimit) return;

  let remaining = charLimit;
  let pastCutoff = false;

  const $visit = (node: LexicalNode): void => {
    if (pastCutoff) {
      node.remove();
      return;
    }

    if ($isTextNode(node)) {
      const text = stripTrailingEllipsis(node.getTextContent());
      if (text.length < remaining) {
        if (text !== node.getTextContent()) node.setTextContent(text);
        remaining -= text.length;
        return;
      }
      if (!$cutInsideUrl(node, text, remaining)) {
        const suffix =
          text.length === remaining ? text : text.slice(0, remaining);
        node.setTextContent(suffix + ELLIPSIS);
      }
      remaining = 0;
      pastCutoff = true;
      return;
    }

    if ($isElementNode(node)) {
      for (const child of node.getChildren()) $visit(child);
      return;
    }

    if ($isDecoratorNode(node)) {
      const len = node.getTextContent().length;
      if (len <= remaining) {
        remaining -= len;
        return;
      }
      node.remove();
      pastCutoff = true;
    }
  };

  for (const child of rootNode.getChildren()) $visit(child);
};

const $restoreRoot = (root: RootNode, serialized: SerializedRootNode): void => {
  root.clear();
  for (const child of serialized.children) {
    root.append($parseSerializedNode(child));
  }
};

export const CharacterLimitPlugin = ({
  expanded,
  charLimit,
  withShowMore,
  setExpanded,
}: {
  expanded: boolean;
  charLimit: number;
  withShowMore?: boolean;
  setExpanded: (expanded: boolean) => void;
}) => {
  const [editor] = useLexicalComposerContext();
  const [initialSerialized, setInitialSerialized] =
    useState<SerializedRootNode | null>(null);
  const [initialBudgetLength, setInitialBudgetLength] = useState(0);

  useLayoutEffect(() => {
    setInitialSerialized(editor.getEditorState().toJSON().root);
    editor.getEditorState().read(() => {
      setInitialBudgetLength($getBudgetTextLength($getRoot()));
    });
  }, [editor]);

  useLayoutEffect(() => {
    if (!initialSerialized) return;

    editor.update(() => {
      const root = $getRoot();
      $restoreRoot(root, initialSerialized);
      $setSelection(null);
      if (!expanded) $truncateRoot(root, charLimit);
    });
  }, [editor, expanded, charLimit, initialSerialized]);

  useLayoutEffect(() => {
    if (!initialSerialized) return;
    return () => {
      editor.update(() => {
        const root = $getRoot();
        $restoreRoot(root, initialSerialized);
        $setSelection(null);
      });
    };
  }, [editor, initialSerialized]);

  const showMoreNeeded = !!withShowMore && initialBudgetLength > charLimit;

  if (!showMoreNeeded) return null;

  return (
    <LinkButton
      onClick={(e) => {
        e.stopPropagation();
        setExpanded(!expanded);
      }}
    >
      {expanded ? 'Show less' : 'Show more'}
    </LinkButton>
  );
};
